import { useState } from 'react';
import { MarkdownRenderer } from './MarkdownRenderer';
import { Edit, Eye } from 'lucide-react';

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  height?: number;
  placeholder?: string;
}

export default function MarkdownEditor({ value, onChange, height = 300, placeholder = 'Enter markdown...' }: MarkdownEditorProps) {
  const [mode, setMode] = useState<'edit' | 'preview'>('edit');

  return (
    <div className="w-full glass-input p-0 overflow-hidden flex flex-col">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-2 py-1.5 border-b border-glass-200/10 bg-glass-100/5">
        <div className="segmented" role="group" aria-label="Editor mode">
          <button
            onClick={() => setMode('edit')}
            data-active={mode === 'edit' ? 'true' : 'false'}
            aria-pressed={mode === 'edit'}
            className="segmented-item segmented-item-sm touch-manipulation"
          >
            <Edit className="w-3.5 h-3.5" />
            <span>Edit</span>
          </button>
          <button
            onClick={() => setMode('preview')}
            data-active={mode === 'preview' ? 'true' : 'false'}
            aria-pressed={mode === 'preview'}
            className="segmented-item segmented-item-sm touch-manipulation"
          >
            <Eye className="w-3.5 h-3.5" />
            <span>Preview</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{ height: `${height}px` }} className="overflow-auto">
        {mode === 'edit' ? (
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="w-full h-full p-3 bg-transparent text-white placeholder:text-glass-500 border-0 outline-none resize-none font-mono text-sm leading-relaxed"
            style={{ minHeight: `${height}px` }}
          />
        ) : (
          <div className="p-3">
            {value ? (
              <MarkdownRenderer content={value} />
            ) : (
              <p className="text-sm text-glass-500 italic">{placeholder}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
